/**
 * NotificationFailuresTable — failed dispatches with retry action.
 *
 * Pulls /v3/notifications/dispatches?outcome=failed and surfaces
 * per-envelope counts. Click-through to the audit "notifications-history"
 * page for full per-recipient detail. Retry button POSTs back to
 * /v3/notifications/dispatch with the same eventCode + recipients to
 * trigger a re-send.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router';
import type {
  NotifDispatchHistoryFilters,
  NotifDispatchRow,
} from '@retail-ops/shared-types';
import { asIso8601 } from '@retail-ops/shared-types';
import { useApiClient } from '@/contexts/ApiClientContext';
import { downloadCsv, formatLocal, humanAgo, rowsToCsv } from '@/utils/time';

type DateRange = '24h' | '7d' | '30d';

function fromUtcFor(range: DateRange): string {
  const d = new Date();
  if (range === '24h') d.setHours(d.getHours() - 24);
  else if (range === '7d') d.setDate(d.getDate() - 7);
  else d.setDate(d.getDate() - 30);
  return d.toISOString();
}

export function NotificationFailuresTable(): JSX.Element {
  const api = useApiClient();
  const [range, setRange] = useState<DateRange>('24h');
  const [rows, setRows] = useState<ReadonlyArray<NotifDispatchRow>>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [retrying, setRetrying] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const filters: NotifDispatchHistoryFilters = {
        outcome: 'failed',
        fromUtc: asIso8601(fromUtcFor(range)),
        top: 200,
      };
      const resp = await api.retail.notifications.dispatches(filters);
      setRows(resp.rows);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [api, range]);

  useEffect(() => {
    void load();
  }, [load]);

  const onRetry = useCallback(
    async (row: NotifDispatchRow): Promise<void> => {
      setRetrying(row.id);
      try {
        // Re-dispatch using the same eventCode; the backend will re-resolve
        // recipients from the original audience. Idempotency-key keeps
        // duplicate envelopes from being created within the dedup window.
        await api.retail.notifications.dispatch({
          eventCode: row.eventCode,
          ...(row.templateCode ? { templateCode: row.templateCode } : {}),
          recipients: [],
          idempotencyKey: `sysops-retry:${row.id}`,
        });
        await load();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Retry failed');
      } finally {
        setRetrying(null);
      }
    },
    [api, load],
  );

  const exportCsv = (): void => {
    if (rows.length === 0) return;
    const csv = rowsToCsv(rows as unknown as ReadonlyArray<Record<string, unknown>>);
    downloadCsv(
      `notification-failures-${new Date().toISOString().slice(0, 10)}.csv`,
      csv,
    );
  };

  return (
    <section className="card">
      <header className="card-header">
        <h3>Notification failures</h3>
        <div className="toolbar__spacer" />
        <label className="toolbar__field">
          Range
          <select value={range} onChange={(e) => setRange(e.target.value as DateRange)}>
            <option value="24h">Last 24 hours</option>
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
          </select>
        </label>
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={() => void load()}
        >
          Refresh
        </button>
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={exportCsv}
          disabled={rows.length === 0}
        >
          Export CSV
        </button>
      </header>

      {loading && <p>Loading…</p>}
      {error && (
        <div className="banner banner--err" role="alert">
          {error}
        </div>
      )}
      {!loading && rows.length === 0 && (
        <div className="empty">No failed dispatches in this window.</div>
      )}

      {rows.length > 0 && (
        <table className="table">
          <thead>
            <tr>
              <th>Event</th>
              <th>Template</th>
              <th>Recipients</th>
              <th>Failed</th>
              <th>Requested</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                <td>
                  <Link to={`/audit/notifications-history?id=${encodeURIComponent(r.id)}`}>
                    {r.eventCode}
                  </Link>
                </td>
                <td>{r.templateCode}</td>
                <td>{r.recipientCount}</td>
                <td>
                  <span className="pill pill--err">{r.failed}</span>
                </td>
                <td>
                  {humanAgo(r.requestedAt)} ({formatLocal(r.requestedAt)})
                </td>
                <td>
                  <button
                    type="button"
                    className="btn btn--ghost btn--sm"
                    onClick={() => void onRetry(r)}
                    disabled={retrying === r.id}
                  >
                    {retrying === r.id ? 'Retrying…' : 'Retry'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
